import { useState, useRef } from 'react';

/**
 * Dialog component for editing blueprint manually
 * Allows users to input coordinates or upload JSON file
 */
export default function BlueprintEditor({ 
  blueprint, 
  onSave, 
  onCancel 
}) {
  const [inputMode, setInputMode] = useState('manual'); // 'manual', 'upload', or 'ruler'
  const [jsonInput, setJsonInput] = useState(JSON.stringify(blueprint || { outlinePoints: [] }, null, 2));
  const [error, setError] = useState(null);
  const [fileInput, setFileInput] = useState(null);
  
  // Ruler mode state
  const [rulerStart, setRulerStart] = useState(null);
  const [rulerEnd, setRulerEnd] = useState(null);
  const [rulerRealLength, setRulerRealLength] = useState('');
  const [rulerUnit, setRulerUnit] = useState('feet'); // 'feet' or 'meters'
  const [calculatedScale, setCalculatedScale] = useState(null);
  const canvasRef = useRef(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const backgroundImageRef = useRef(null);

  const handleSave = () => {
    try {
      let parsedBlueprint;
      
      if (inputMode === 'manual') {
        parsedBlueprint = JSON.parse(jsonInput);
      } else {
        // Handle file upload
        if (!fileInput) {
          setError('Please select a file to upload');
          return;
        }
        
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            parsedBlueprint = JSON.parse(e.target.result);
            validateAndSave(parsedBlueprint);
          } catch (err) {
            setError('Invalid JSON file: ' + err.message);
          }
        };
        reader.readAsText(fileInput);
        return; // Will continue in reader.onload
      }
      
      validateAndSave(parsedBlueprint);
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
    }
  };

  const validateAndSave = (parsedBlueprint) => {
    // Validate blueprint structure
    if (!parsedBlueprint || typeof parsedBlueprint !== 'object') {
      setError('Blueprint must be an object');
      return;
    }

    // Ensure outlinePoints is an array
    if (!parsedBlueprint.outlinePoints || !Array.isArray(parsedBlueprint.outlinePoints)) {
      setError('Blueprint must have an outlinePoints array');
      return;
    }

    // Validate outlinePoints
    if (parsedBlueprint.outlinePoints.length < 3) {
      setError('outlinePoints must have at least 3 points');
      return;
    }

    // Validate each point has x and y
    for (let i = 0; i < parsedBlueprint.outlinePoints.length; i++) {
      const pt = parsedBlueprint.outlinePoints[i];
      if (typeof pt.x !== 'number' || typeof pt.y !== 'number') {
        setError(`Point ${i + 1} must have numeric x and y properties`);
        return;
      }
    }

    setError(null);
    onSave(parsedBlueprint);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        setError('Please select a JSON file');
        return;
      }
      setFileInput(file);
      setError(null);
    }
  };

  const handleLoadExample = () => {
    const example = {
      outlinePoints: [
        { x: 0, y: 0 },
        { x: 100, y: 0 },
        { x: 100, y: 100 },
        { x: 0, y: 100 }
      ],
      offices: [],
      bathrooms: [],
      hallways: [],
      conferenceRooms: [],
      walls: []
    };
    setJsonInput(JSON.stringify(example, null, 2));
    setError(null);
  };

  // Ruler mode handlers
  const handleCanvasClick = (e) => {
    if (inputMode !== 'ruler') return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    if (!rulerStart) {
      setRulerStart({ x, y });
      setRulerEnd(null);
      setCalculatedScale(null);
    } else {
      setRulerEnd({ x, y });
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (inputMode !== 'ruler' || !rulerStart || rulerEnd) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    // Redraw canvas with temporary line
    drawRuler(rulerStart, { x, y }, false);
  };

  const drawRuler = (start, end, isComplete) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Clear canvas with dark background
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw background image if available
    if (backgroundImage && backgroundImageRef.current) {
      const img = backgroundImageRef.current;
      if (img.complete && img.naturalWidth > 0) {
        // Calculate scale to fit image in canvas
        const imgScale = Math.min(
          canvas.width / img.naturalWidth,
          canvas.height / img.naturalHeight
        );
        const imgWidth = img.naturalWidth * imgScale;
        const imgHeight = img.naturalHeight * imgScale;
        const imgX = (canvas.width - imgWidth) / 2;
        const imgY = (canvas.height - imgHeight) / 2;
        
        ctx.globalAlpha = 0.4; // Semi-transparent background
        ctx.drawImage(img, imgX, imgY, imgWidth, imgHeight);
        ctx.globalAlpha = 1.0;
      }
    }
    
    // Draw blueprint outline if available - EXACTLY as it appears in warehouse view
    if (blueprint?.outlinePoints && blueprint.outlinePoints.length > 0) {
      const points = blueprint.outlinePoints;
      
      // Calculate bounds (handle negative coordinates!)
      const xs = points.map(p => p.x);
      const ys = points.map(p => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      
      const blueprintWidth = maxX - minX;
      const blueprintHeight = maxY - minY;
      
      // Calculate scale to fit in canvas with padding
      const padding = 40;
      const scale = Math.min(
        (canvas.width - padding * 2) / blueprintWidth,
        (canvas.height - padding * 2) / blueprintHeight
      );
      
      // Center the blueprint - accounting for negative coordinates
      const scaledWidth = blueprintWidth * scale;
      const scaledHeight = blueprintHeight * scale;
      // Translate so minX,minY maps to the padding area
      const offsetX = padding - minX * scale;
      const offsetY = padding - minY * scale;
      
      // Center it
      const centerOffsetX = (canvas.width - scaledWidth - padding * 2) / 2;
      const centerOffsetY = (canvas.height - scaledHeight - padding * 2) / 2;
      const finalOffsetX = offsetX + centerOffsetX;
      const finalOffsetY = offsetY + centerOffsetY;
      
      // Store transform for ruler clicks
      canvas.dataset.scale = scale;
      canvas.dataset.offsetX = finalOffsetX;
      canvas.dataset.offsetY = finalOffsetY;
      canvas.dataset.minX = minX;
      canvas.dataset.minY = minY;
      
      // Draw grid AFTER we know the scale
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1;
      const gridSize = 100 * scale; // Grid every 100 units in blueprint space
      const startX = (Math.floor(minX / 100) * 100 - minX) * scale + finalOffsetX;
      const startY = (Math.floor(minY / 100) * 100 - minY) * scale + finalOffsetY;
      
      for (let x = startX; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = startY; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
      
      // Draw filled outline - matching warehouse view exactly with RECTILINEAR paths
      ctx.fillStyle = 'rgba(59, 130, 246, 0.06)';
      ctx.strokeStyle = '#60a5fa';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'miter';
      ctx.beginPath();
      
      // Start at first point
      const firstX = points[0].x * scale + finalOffsetX;
      const firstY = points[0].y * scale + finalOffsetY;
      ctx.moveTo(firstX, firstY);
      
      // Draw rectilinear path (vertical then horizontal between points)
      for (let i = 1; i < points.length; i++) {
        const prevPoint = points[i - 1];
        const currPoint = points[i];
        
        const prevX = prevPoint.x * scale + finalOffsetX;
        const prevY = prevPoint.y * scale + finalOffsetY;
        const currX = currPoint.x * scale + finalOffsetX;
        const currY = currPoint.y * scale + finalOffsetY;
        
        // If already aligned horizontally or vertically, draw direct line
        if (prevPoint.x === currPoint.x || prevPoint.y === currPoint.y) {
          ctx.lineTo(currX, currY);
        } else {
          // Otherwise, go vertical first, then horizontal (matching WarehouseMap.js line 294)
          const cornerX = prevX;
          const cornerY = currY;
          ctx.lineTo(cornerX, cornerY);
          ctx.lineTo(currX, currY);
        }
      }
      
      // Close path back to first point with rectilinear bend
      const lastPoint = points[points.length - 1];
      const lastX = lastPoint.x * scale + finalOffsetX;
      const lastY = lastPoint.y * scale + finalOffsetY;
      
      if (lastPoint.x === points[0].x || lastPoint.y === points[0].y) {
        ctx.lineTo(firstX, firstY);
      } else {
        const closeCornerX = lastX;
        const closeCornerY = firstY;
        ctx.lineTo(closeCornerX, closeCornerY);
        ctx.lineTo(firstX, firstY);
      }
      
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Draw corner markers
      points.forEach((point) => {
        const x = point.x * scale + finalOffsetX;
        const y = point.y * scale + finalOffsetY;
        
        // Outer circle (blue)
        ctx.fillStyle = '#3b82f6';
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fill();
        
        // Inner dot (white)
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, 2 * Math.PI);
        ctx.fill();
      });
      
      // Draw scale info
      ctx.fillStyle = '#9ca3af';
      ctx.font = '11px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`Display scale: ${scale.toFixed(3)}x`, 10, canvas.height - 10);
      ctx.fillText(`Blueprint: ${blueprintWidth.toFixed(0)}px × ${blueprintHeight.toFixed(0)}px`, 10, canvas.height - 25);
      ctx.fillText(`Bounds: (${minX.toFixed(0)}, ${minY.toFixed(0)}) to (${maxX.toFixed(0)}, ${maxY.toFixed(0)})`, 10, canvas.height - 40);
    }
    
    if (!start || !end) return;
    
    // Draw ruler line
    ctx.strokeStyle = isComplete ? '#10b981' : '#f59e0b';
    ctx.lineWidth = 3;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Draw endpoints
    ctx.fillStyle = isComplete ? '#10b981' : '#f59e0b';
    ctx.beginPath();
    ctx.arc(start.x, start.y, 6, 0, 2 * Math.PI);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(end.x, end.y, 6, 0, 2 * Math.PI);
    ctx.fill();
    
    // Calculate and display pixel distance IN BLUEPRINT SPACE (not canvas space)
    const displayScale = parseFloat(canvas?.dataset?.scale || 1);
    
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const canvasPixelDistance = Math.sqrt(dx * dx + dy * dy);
    
    // Convert to blueprint space (actual map pixels)
    const blueprintPixelDistance = canvasPixelDistance / displayScale;
    
    // Draw distance label
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;
    
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(midX - 60, midY - 20, 120, 30);
    ctx.strokeStyle = isComplete ? '#10b981' : '#f59e0b';
    ctx.strokeRect(midX - 60, midY - 20, 120, 30);
    
    ctx.fillStyle = '#ffffff';
    ctx.font = '13px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${blueprintPixelDistance.toFixed(1)}px`, midX, midY - 5);
  };

  const calculateScale = () => {
    if (!rulerStart || !rulerEnd || !rulerRealLength) {
      setError('Please draw a ruler line and enter the real-world measurement');
      return;
    }
    
    const realLength = parseFloat(rulerRealLength);
    if (isNaN(realLength) || realLength <= 0) {
      setError('Please enter a valid positive number for the length');
      return;
    }
    
    const canvas = canvasRef.current;
    const displayScale = parseFloat(canvas.dataset.scale || 1);
    
    // Calculate pixel distance in CANVAS space
    const dx = rulerEnd.x - rulerStart.x;
    const dy = rulerEnd.y - rulerStart.y;
    const canvasPixelDistance = Math.sqrt(dx * dx + dy * dy);
    
    // Convert to BLUEPRINT space (actual pixels)
    const blueprintPixelDistance = canvasPixelDistance / displayScale;
    
    // Convert to meters if needed
    const lengthInMeters = rulerUnit === 'feet' ? realLength * 0.3048 : realLength;
    
    const pixelsPerMeter = blueprintPixelDistance / lengthInMeters;
    const metersPerPixel = lengthInMeters / blueprintPixelDistance;
    
    setCalculatedScale({
      pixelsPerMeter: pixelsPerMeter.toFixed(2),
      metersPerPixel: metersPerPixel.toFixed(4),
      pixelDistance: blueprintPixelDistance.toFixed(1),
      realLength: realLength,
      unit: rulerUnit
    });
    
    setError(null);
    drawRuler(rulerStart, rulerEnd, true);
  };

  const resetRuler = () => {
    setRulerStart(null);
    setRulerEnd(null);
    setRulerRealLength('');
    setCalculatedScale(null);
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      drawRuler(null, null, false);
    }
  };

  const saveScaleToBlueprint = () => {
    if (!calculatedScale) {
      setError('Please calculate the scale first');
      return;
    }
    
    const updatedBlueprint = {
      ...blueprint,
      scale: {
        pixelsPerMeter: parseFloat(calculatedScale.pixelsPerMeter),
        metersPerPixel: parseFloat(calculatedScale.metersPerPixel),
        calibrationLength: calculatedScale.realLength,
        calibrationUnit: calculatedScale.unit,
        calibratedAt: new Date().toISOString()
      }
    };
    
    onSave(updatedBlueprint);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl p-6 max-w-5xl w-full border border-gray-700 shadow-2xl max-h-[95vh] flex flex-col overflow-hidden">
        <h2 className="text-xl font-bold text-white mb-4">Edit Blueprint</h2>

        {/* Mode Selection */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setInputMode('manual')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              inputMode === 'manual'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Manual Input
          </button>
          <button
            onClick={() => setInputMode('upload')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              inputMode === 'upload'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Upload JSON
          </button>
          <button
            onClick={() => {
              setInputMode('ruler');
              setTimeout(() => drawRuler(null, null, false), 100);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              inputMode === 'ruler'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            📏 Calibrate Scale
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4 text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Manual Input Mode */}
        {inputMode === 'manual' && (
          <div className="flex-1 flex flex-col mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-300">Blueprint JSON:</label>
              <button
                onClick={handleLoadExample}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Load Example
              </button>
            </div>
            <textarea
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setError(null);
              }}
              className="flex-1 w-full bg-gray-900 text-gray-100 font-mono text-sm p-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none resize-none"
              placeholder='{"outlinePoints": [{"x": 0, "y": 0}, ...]}'
            />
            <div className="text-xs text-gray-400 mt-2">
              Format: JSON object with outlinePoints array. Each point needs x and y coordinates.
            </div>
          </div>
        )}

        {/* Upload Mode */}
        {inputMode === 'upload' && (
          <div className="flex-1 flex flex-col mb-4">
            <label className="text-sm font-medium text-gray-300 mb-2">Select JSON File:</label>
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-500 file:cursor-pointer"
            />
            {fileInput && (
              <div className="mt-2 text-sm text-gray-400">
                Selected: {fileInput.name} ({(fileInput.size / 1024).toFixed(2)} KB)
              </div>
            )}
            <div className="text-xs text-gray-400 mt-2">
              Upload a JSON file containing blueprint data with outlinePoints array.
            </div>
          </div>
        )}

        {/* Ruler/Calibration Mode */}
        {inputMode === 'ruler' && (
          <div className="flex-1 flex flex-col mb-4">
            <div className="mb-3">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Instructions:</h3>
              <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
                <li>Click on the canvas to set the start point of your measurement</li>
                <li>Click again to set the end point</li>
                <li>Enter the real-world length of that distance</li>
                <li>Click "Calculate Scale" to see the conversion factor</li>
                <li>Click "Save Scale" to apply it to your blueprint</li>
              </ol>
            </div>

            {/* Background image upload */}
            {blueprint?.imageUrl && (
              <img 
                ref={backgroundImageRef}
                src={blueprint.imageUrl} 
                alt="Blueprint background"
                style={{ display: 'none' }}
                onLoad={() => {
                  setBackgroundImage(blueprint.imageUrl);
                  setTimeout(() => drawRuler(rulerStart, rulerEnd, false), 100);
                }}
              />
            )}

            {/* Canvas for drawing ruler */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 p-2 mb-3">
              <canvas
                ref={canvasRef}
                width={1200}
                height={700}
                onClick={handleCanvasClick}
                onMouseMove={handleCanvasMouseMove}
                className="w-full cursor-crosshair rounded"
                style={{ maxHeight: '700px', imageRendering: 'auto' }}
              />
              <div className="text-xs text-gray-400 mt-2 text-center">
                Click to place ruler start point, then click again for end point
              </div>
            </div>

            {/* Ruler controls */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Real-world Length:
                </label>
                <input
                  type="number"
                  value={rulerRealLength}
                  onChange={(e) => setRulerRealLength(e.target.value)}
                  placeholder="e.g., 100"
                  step="0.1"
                  min="0"
                  className="w-full bg-gray-800 text-gray-100 text-sm px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Unit:
                </label>
                <select
                  value={rulerUnit}
                  onChange={(e) => setRulerUnit(e.target.value)}
                  className="w-full bg-gray-800 text-gray-100 text-sm px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                >
                  <option value="feet">Feet</option>
                  <option value="meters">Meters</option>
                </select>
              </div>
            </div>

            {/* Action buttons for ruler */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={calculateScale}
                disabled={!rulerStart || !rulerEnd || !rulerRealLength}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
              >
                Calculate Scale
              </button>
              <button
                onClick={resetRuler}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors"
              >
                Reset
              </button>
            </div>

            {/* Calculated scale display */}
            {calculatedScale && (
              <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-green-300 mb-2">✓ Scale Calculated:</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-gray-400">Pixel Distance:</div>
                  <div className="text-gray-200 font-mono">{calculatedScale.pixelDistance} pixels</div>
                  
                  <div className="text-gray-400">Real Distance:</div>
                  <div className="text-gray-200 font-mono">{calculatedScale.realLength} {calculatedScale.unit}</div>
                  
                  <div className="text-gray-400">Pixels per Meter:</div>
                  <div className="text-gray-200 font-mono">{calculatedScale.pixelsPerMeter}</div>
                  
                  <div className="text-gray-400">Meters per Pixel:</div>
                  <div className="text-gray-200 font-mono">{calculatedScale.metersPerPixel}</div>
                </div>
                <button
                  onClick={saveScaleToBlueprint}
                  className="w-full mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Save Scale to Blueprint
                </button>
              </div>
            )}

            {/* Current scale info if exists */}
            {blueprint?.scale && inputMode === 'ruler' && !calculatedScale && (
              <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3 text-xs">
                <div className="text-blue-300 font-medium mb-1">Current Scale:</div>
                <div className="text-gray-300">
                  {blueprint.scale.pixelsPerMeter.toFixed(2)} pixels/meter
                  {blueprint.scale.calibrationLength && (
                    <span className="text-gray-400">
                      {' '}(calibrated with {blueprint.scale.calibrationLength} {blueprint.scale.calibrationUnit})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Debug info */}
            {inputMode === 'ruler' && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-2 text-xs space-y-1">
                <div className="text-gray-400">
                  <span className="font-semibold">Blueprint data:</span>
                </div>
                <div className="text-gray-500 font-mono">
                  Outline points: {blueprint?.outlinePoints?.length || 0}
                  {blueprint?.outlinePoints?.length > 0 && (
                    <span> (e.g., x:{blueprint.outlinePoints[0].x}, y:{blueprint.outlinePoints[0].y})</span>
                  )}
                </div>
                <div className="text-gray-500 font-mono">
                  Image URL: {blueprint?.imageUrl ? '✓ Present' : '✗ None'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-700">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          {inputMode !== 'ruler' && (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Save Blueprint
            </button>
          )}
        </div>
      </div>
    </div>
  );
}




